class CustomDealerList extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.pinnedDealer = null;
    this.pinnedDealerId = "";
    this.notFoundText = this.getAttribute("not-found-text") || "No Results Found.";
    this.unit = this.getAttribute("unit")? this.getAttribute("unit") : " km";
    this.visitWebsiteText = this.getAttribute("visit-website-text") || "Visit Website";
    this._appendChunkSize = 25;
  }

  connectedCallback() {
    if (this._clickDelegated) return;
    this._clickDelegated = true;
    this.addEventListener("click", (e) => this._handleDelegatedClick(e));
  }

  set data(newData) {
    this.items = Array.isArray(newData) ? newData : [];
    this.pinnedDealer = null;
    this.pinnedDealerId = "";
    this.render();
  }

  appendData(newData) {
    const items = Array.isArray(newData) ? newData : [];
    if (items.length === 0) return Promise.resolve();

    this.items = this.items.concat(items);
    return this._appendItemsToContainer(items);
  }

  appendDataAsync(newData, chunkSize = this._appendChunkSize) {
    const items = Array.isArray(newData) ? newData : [];
    if (items.length === 0) return Promise.resolve();

    this.items = this.items.concat(items);
    return this._appendItemsToContainer(items, chunkSize);
  }

  _getListContainer() {
    let container = this._container || this.querySelector(":scope > div");
    const isEmptyState =
      container &&
      !this.querySelector(".custom-dealer-item") &&
      container.querySelector(".text-center");

    if (!container || isEmptyState) {
      this.render();
      container = this._container;
    }
    return container;
  }

  _appendItemsToContainer(items, chunkSize = items.length) {
    const container = this._getListContainer();
    if (!container) return Promise.resolve();

    if (chunkSize >= items.length) {
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(this._renderNormalItem(item)));
      container.appendChild(fragment);
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      let index = 0;

      const appendChunk = () => {
        const end = Math.min(index + chunkSize, items.length);
        const fragment = document.createDocumentFragment();
        for (; index < end; index++) {
          fragment.appendChild(this._renderNormalItem(items[index]));
        }
        container.appendChild(fragment);

        if (index < items.length) {
          requestAnimationFrame(appendChunk);
        } else {
          resolve();
        }
      };

      requestAnimationFrame(appendChunk);
    });
  }

  render() {
    this.innerHTML = "";
    const container = document.createElement("div");
    this._container = container;

    if (this.items.length === 0 && !this.pinnedDealer) {
      container.innerHTML = this._renderEmpty();
    } else {
      const fragment = document.createDocumentFragment();
      if (this.pinnedDealer) {
        fragment.appendChild(this._renderPinnedItem(this.pinnedDealer));
      }
      this.items.forEach(item => {
        fragment.appendChild(this._renderNormalItem(item));
      });
      container.appendChild(fragment);
    }

    this.appendChild(container);
  }

  setPinnedDealer(item) {
    if (!item || !item.dealerId) return null;

    const container = this._getListContainer();
    if (!container) return null;

    container.querySelectorAll(".custom-dealer-item--pinned-duplicate").forEach((el) => {
      el.classList.remove("custom-dealer-item--pinned-duplicate");
      el.hidden = false;
    });
    container.querySelector(".custom-dealer-item--pinned")?.remove();

    this.pinnedDealer = item;
    this.pinnedDealerId = String(item.dealerId);

    if (!container.querySelector(".custom-dealer-item") && container.querySelector(".text-center")) {
      container.innerHTML = "";
    }

    container.querySelectorAll(".custom-dealer-item").forEach((el) => {
      if (el.dataset.dealerid === this.pinnedDealerId) {
        el.classList.add("custom-dealer-item--pinned-duplicate");
        el.hidden = true;
      }
    });

    const pinnedItem = this._renderPinnedItem(item);
    container.prepend(pinnedItem);
    this._setActiveDealer(this.pinnedDealerId);
    this.scrollTo({ top: 0, behavior: "smooth" });
    return pinnedItem;
  }

  _isPinnedDealer(item) {
    return Boolean(this.pinnedDealerId && item && String(item.dealerId) === this.pinnedDealerId);
  }

  _renderPinnedItem(item) {
    const el = this._renderItem(item);
    el.classList.add("custom-dealer-item--pinned", "active");
    return el;
  }

  _renderNormalItem(item) {
    const el = this._renderItem(item);
    if (this._isPinnedDealer(item)) {
      el.classList.add("custom-dealer-item--pinned-duplicate");
      el.hidden = true;
    }
    return el;
  }

  _setActiveDealer(dealerId) {
    this.querySelectorAll(".custom-dealer-item").forEach(el => {
      el.classList.toggle("active", el.dataset.dealerid === String(dealerId));
    });
  }

  _renderEmpty() {
    return `
      <div class="text-center text-base">
        <div class="flex items-center justify-center mb-2">
          <svg width="45" height="45" viewBox="0 0 45 45" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.2203 30.039C8.09324 24.883 8.09324 16.5231 13.2203 11.367C18.3455 6.21098 26.6552 6.21098 31.7804 11.367C36.9065 16.524 36.9065 24.8839 31.7804 30.0409L22.5013 39.375L13.2203 30.039ZM26.4781 24.7047C27.0004 24.1792 27.4148 23.5553 27.6975 22.8687C27.9802 22.1821 28.1257 21.4462 28.1257 20.703C28.1257 19.9599 27.9802 19.2239 27.6975 18.5373C27.4148 17.8507 27.0004 17.2269 26.4781 16.7014C25.9557 16.1758 25.3356 15.759 24.6531 15.4746C23.9706 15.1902 23.2391 15.0438 22.5004 15.0438C21.7616 15.0438 21.0301 15.1902 20.3476 15.4746C19.6651 15.759 19.045 16.1758 18.5226 16.7014C17.4677 17.7627 16.875 19.2021 16.875 20.703C16.875 22.2039 17.4677 23.6434 18.5226 24.7047C19.5776 25.766 21.0084 26.3623 22.5004 26.3623C23.9923 26.3623 25.4231 25.766 26.4781 24.7047Z" fill="#B3B3B3"/>
            <circle cx="32.5" cy="34.5" r="7.5" fill="#FF5A00"/>
            <path d="M33.0725 34.5641L35.0196 36.51C35.1602 36.6506 35.1602 36.879 35.0196 37.0196C34.879 37.1602 34.6506 37.1602 34.51 37.0196L32.5641 35.0725C32.5292 35.0377 32.4719 35.0377 32.437 35.0725L30.49 37.0196C30.3494 37.1602 30.121 37.1602 29.9804 37.0196C29.8399 36.879 29.8399 36.6506 29.9804 36.51L31.9275 34.563C31.9623 34.5281 31.9623 34.4708 31.9275 34.4359L29.9804 32.49C29.8399 32.3494 29.8399 32.121 29.9804 31.9804C30.121 31.8399 30.3494 31.8399 30.49 31.9804L32.437 33.9275C32.4719 33.9623 32.5292 33.9623 32.5641 33.9275L34.51 31.9804C34.6506 31.8399 34.879 31.8399 35.0196 31.9804C35.1602 32.121 35.1602 32.3494 35.0196 32.49L33.0725 34.4359C33.0558 34.453 33.0464 34.476 33.0464 34.5C33.0464 34.524 33.0558 34.547 33.0725 34.5641Z" fill="white"/>
          </svg>
        </div>
        <div class="font-[400] text-[#434343] text-[14px]">${this.notFoundText}</div>
      </div>`;
  }

  _renderItem(item) {
    const el = document.createElement("div");
    el.classList.add("custom-dealer-item");
    el._dealerData = item;
    el.dataset.lat = item.latitude;
    el.dataset.long = item.longitude;
    el.dataset.name = item.name;
    el.dataset.dealerid = item.dealerId;
    el.dataset.distanceinmeters = item.distanceInMeters;
    el.dataset.cornermark = item.cornerMark;

    el.innerHTML = `
      <div class="text-base custom-dealer-item-title font-bold flex justify-between items-center gap-4">
        <div class="text-[#434343] leading-tight">${item.name}</div>
        ${this._renderStatusIcons()}
      </div>
      ${item.cornerMark && item.cornerMark !== "null" ? this._renderCornerMark(item.cornerMark) : ""}
      <hr class="bg-[#D9D9D9]" />
      <div class="flex flex-col gap-y-2.5">
        ${this._renderTags(item.productFilter)}
        ${item.phone && item.phone !== "null" ? this._renderPhone(item.phone,item.name,item.dealerId) : ""}
        ${item.email && item.email !== "null" ? this._renderEmail(item.email,item.name,item.dealerId) : ""}
        ${item.address && item.address !== "null" ? this._renderAddress(item) : ""}
      </div>
      <div class="flex ${item.website && item.website !== "null" ? "justify-between" : "justify-end"} items-center">
        ${item.website && item.website !== "null" ? this._renderWebsite(item.website,item.name,item.dealerId) : ""}
        ${item.distanceInMeters != null ? this._renderDistance(item.distanceInMeters) : ""}
      </div>
    `;
    return el;
  }

  _renderCornerMark(cornerMark) {
    return `<div class="flex items-center flex-wrap gap-[6px]">
      ${cornerMark
        .split(";")
        .filter((tag) => tag.trim())
        .map(
          (tag) =>
            `<div class='py-[2px] px-[8px] rounded-[4px] bg-[#FF5A0014] text-[12px] font-[600] leading-[150%] tracking-[-0.12px] text-[#FF5A00] w-fit'>${tag}</div>`,
        )
        .join("")}
    </div>`;
  }

  _renderStatusIcons() {
    return `
      <svg class="dealer-item-checked flex-shrink-0" width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12.6748" r="12" fill="#FF5A00"/>
        <path d="M10.6913 14.9869L16.4479 7.875L17.3997 8.69143L10.7748 16.875L7.19971 13.1946L8.07499 12.2934L10.6913 14.9869Z" fill="white"/>
      </svg>
      <svg class="dealer-item-uncheck flex-shrink-0" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="11.5" stroke="#D9D9D9"/>
      </svg>`;
  }

  _renderTags(productFilter) {
    if (!productFilter || productFilter === "null") return "";
    return `<div class="flex gap-1 flex-wrap">
      ${productFilter.split(";").map(tag => `<span class='custom-product-models-tag px-[4px] py-[2px] align-middle text-[10px] text-black rounded-full'>${tag}</span>`).join("")}
    </div>`;
  }

  _renderPhone(phone,name,dealerId) {
    return `<a href="tel:${phone}" class="render-phone text-base flex gap-x-2 custom-dealer-item-info" data-store-name="${name}" data-store-id="${dealerId}">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9.81999 9.90422L9.27997 10.3237C9.27997 10.3237 8.73995 10.5896 8.09017 9.98104C7.44186 9.37097 6.29011 7.97655 5.96522 7.17741C5.64033 6.3768 6.29011 6.0341 6.29011 6.0341C6.29011 6.0341 7.13599 5.2911 7.17112 5.15815C7.2077 5.02521 7.67601 4.49048 6.66768 3.06208C5.65936 1.63221 5.4252 1.91877 4.88519 2.35748C4.34517 2.79472 3.58709 3.65294 3.42611 4.64558C3.26367 5.63675 3.15683 6.85687 4.52517 9.02975C5.89351 11.2026 7.96431 12.8615 9.11606 13.1274C10.2707 13.3932 11.08 13.5646 12.2303 12.5749C13.3835 11.5837 13.5826 11.4892 13.0586 10.7831C12.5406 10.0756 11.3917 9.23064 11.2191 9.19519C11.0478 9.15826 10.8005 9.0977 10.4127 9.40199C10.0249 9.70481 9.81999 9.90422 9.81999 9.90422Z" fill="#FF5A00"/>
      </svg>

      <span>${phone}</span>
    </a>`;
  }

  _renderEmail(email,name,dealerId) {
    return `<a href="mailto:${email}" class="render-email text-base flex gap-x-2 custom-dealer-item-info" data-store-name="${name}" data-store-id="${dealerId}">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M13.3235 5.33928L7.93003 8.431L2.67099 5.41517C2.72194 4.62537 3.38286 4 4.19096 4H11.8092C12.5918 4 13.2372 4.58462 13.3235 5.33928ZM13.3334 6.44668V11.1531C13.3334 11.9893 12.6513 12.6667 11.8092 12.6667H4.19096C3.34889 12.6667 2.66675 11.9893 2.66675 11.1531V8.83995V6.52678L7.68944 9.4063C7.86209 9.50468 8.07014 9.48641 8.22015 9.37679C8.22723 9.37398 8.23289 9.36976 8.23996 9.36555L13.3334 6.44668Z" fill="#FF5A00"/>
      </svg>
      <span>${email}</span>
    </a>`;
  }

  _renderAddress(item) {
    return `<a target="_blank" href="https://www.google.com/maps/dir/?api=1&destination=${Number(item.latitude)},${Number(item.longitude)}"
    class="render-address wtb-navigation text-base flex gap-x-2 custom-dealer-item-info"
    data-store-name="${item.name}"
    data-store-id="${item.dealerId}">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.70047 10.6809C2.87751 8.8476 2.87751 5.87521 4.70047 4.04194C6.52275 2.20868 9.47734 2.20868 11.2996 4.04194C13.1222 5.87554 13.1222 8.84793 11.2996 10.6815L8.00038 14.0003L4.70047 10.6809ZM9.41434 8.78422C9.60007 8.59737 9.7474 8.37555 9.84791 8.13143C9.94843 7.8873 10.0002 7.62564 10.0002 7.3614C10.0002 7.09716 9.94843 6.83551 9.84791 6.59138C9.7474 6.34725 9.60007 6.12543 9.41434 5.93858C9.22861 5.75174 9.00812 5.60352 8.76545 5.5024C8.52279 5.40128 8.2627 5.34923 8.00004 5.34923C7.73738 5.34923 7.4773 5.40128 7.23463 5.5024C6.99197 5.60352 6.77148 5.75174 6.58575 5.93858C6.21065 6.31594 5.99993 6.82774 5.99993 7.3614C5.99993 7.89506 6.21065 8.40687 6.58575 8.78422C6.96084 9.16157 7.46958 9.37357 8.00004 9.37357C8.53051 9.37357 9.03924 9.16157 9.41434 8.78422Z" fill="#FF5A00"/>
      </svg>
      <span>${item.address}</span>
    </a>`;
  }


  _renderWebsite(website,storeName,storeId) {
    return `<a href="${website}" data-store-name="${storeName}"
      data-store-id="${storeId}" class="render-website text-[10px] text-[#8C8C8C] rounded-full border border-[#8C8C8C] px-[8px] py-[4px]">${this.visitWebsiteText}</a>`;
  }

  _renderDistance(distance) {
    let distanceStr = ""
    if(this.unit=== ' miles' && distance){
      distanceStr = Math.round(distance) + this.unit
    }
    if(this.unit=== ' km' && distance){
      distanceStr = Math.round(distance/1000) + this.unit
    }
    return `<div class="flex items-center gap-1">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12.6667 3.33301L2 7.75785L6.41231 9.5962L8.16867 13.9997L12.6667 3.33301Z" fill="#8C8C8C"/>
      </svg>
      <span class="text-sm">${distanceStr}</span>
    </div>`;
  }
  // 👇 放在类内部任意位置（推荐放在 _addClickHandlers 上方）
  _pushGA4Event(eventName, storeName = "", buttonName = "") {
    window.dataLayer = window.dataLayer || [];
    const params = {
      event: 'ga4Event',
      event_name: eventName,
      event_parameters: {
        current_page: window.location.href,
        heading: "",
      }
    }
    if (storeName) {
      params.event_parameters.title = storeName;
    }
    if (buttonName) {
      params.event_parameters.button_name = buttonName;
    }
    window.dataLayer.push(params);
  }


  _handleDelegatedClick(e) {
    const website = e.target.closest(".render-website");
    if (website && this.contains(website)) {
      this._pushGA4Event("map_card_website_click", website.dataset.storeName || "", website.textContent.trim() || "");
      return;
    }

    const address = e.target.closest(".render-address.wtb-navigation");
    if (address && this.contains(address)) {
      this._pushGA4Event("map_card_address_click", "", "Directions");
      return;
    }

    const phone = e.target.closest(".render-phone");
    if (phone && this.contains(phone)) {
      this._pushGA4Event("map_card_phone_click", phone.dataset.storeName || "");
      return;
    }

    const email = e.target.closest(".render-email");
    if (email && this.contains(email)) {
      this._pushGA4Event("map_card_mail_click", email.dataset.storeName || "");
      return;
    }

    const item = e.target.closest(".custom-dealer-item");
    if (!item || !this.contains(item) || e.target.closest("a")) return;

    this._handleItemClick(item);
  }

  _handleItemClick(item) {
    const dealerId = item.dataset.dealerid;
    const dealerData =
      item._dealerData ||
      this.items.find(d => d.dealerId == dealerId) ||
      (this.pinnedDealerId === String(dealerId) ? this.pinnedDealer : null);
    this.dispatchEvent(new CustomEvent('dealer-selected', {
      detail: dealerData,
      bubbles: true
    }));

    if (item.classList.contains("active")) return;
    this._setActiveDealer(item.dataset.dealerid);

    if (typeof map === "undefined" || !map) return;

    map.setZoom(13);
    map.setCenter({
      lat: Number(item.dataset.lat),
      lng: Number(item.dataset.long),
    });

    const marker = typeof markers !== "undefined"
      ? markers.find(m => m.dealerid == dealerId)
      : null;

    if (marker && typeof commonInfoWindow !== "undefined" && commonInfoWindow) {
      commonInfoWindow.setContent(this._renderInfoWindow({
        name: item.dataset.name,
        latitude: Number(item.dataset.lat),
        longitude: Number(item.dataset.long),
        distanceInMeters: item.dataset.distanceinmeters,
        dealerId: item.dataset.dealerid
      }));
      if (typeof currentInfoWindow !== "undefined" && currentInfoWindow) currentInfoWindow.close();
      commonInfoWindow.open(map, marker);
      if (typeof currentInfoWindow !== "undefined") currentInfoWindow = commonInfoWindow;
      this._pushGA4Event("map_card_click", item.dataset.name);
      google.maps.event.addListenerOnce(commonInfoWindow, 'domready', () => {
        this._addInfoWindowNavigationClickHandlers();
      });
    }
  }

  _addInfoWindowNavigationClickHandlers() {
    // 谷歌地图 infoWindow 容器
    const container = document.querySelector('.gm-style-iw');
    if (!container) return;

    container.querySelectorAll('.render-info.wtb-navigation').forEach(el => {
      el.onclick = null; // 防止重复绑定
      el.addEventListener('click', e => {
        e.stopPropagation();
        const storeName = el.dataset.storeName || "";
        const storeId = el.dataset.storeId || "";
        const lat = el.dataset.lat || "";
        const lng = el.dataset.lng || "";
        this._pushGA4Event("map_directions_click", storeName)
        window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
      });
    });
  }

  _renderInfoWindow(location) {
    let distance = "";
    if(this.unit === ' miles' && location.distanceInMeters){
      distance = Math.round(location.distanceInMeters) + this.unit;
    }
    if(this.unit === ' km' && location.distanceInMeters){
      distance = Math.round(location.distanceInMeters / 1000) + this.unit;
    }
    return `
      <div class="flex items-center gap-4">
        <div class="text-[16px] text-[#434343]">${location.name}</div>
        <div class="wtb-navigation render-info flex flex-col items-center gap-0.5 cursor-pointer"
             data-store-name="${location.name}"
             data-store-id="${location.dealerId || ''}"
             data-lat="${location.latitude}"
             data-lng="${location.longitude}">
          <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16.3535" cy="16" r="16" fill="#FF5A00"/>
            <path d="M23.3535 9L7.35352 15.6373L13.972 18.3948L16.6065 25L23.3535 9Z" fill="white"/>
          </svg>
          <div class="text-[14px] text-[#434343] font-normal">${distance}</div>
        </div>
      </div>
    `;
  }
}

customElements.define("custom-dealer-list", CustomDealerList);
